// src/frontend_react/src/App.js
import React from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation, Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/App.css';

// Context and Hooks
import { useAuth } from './contexts/AuthContext';

// Base UI Components
import Button from './components/ui/Button/Button';
import Spinner from './components/ui/Spinner/Spinner';
import Typography from './components/ui/Typography/Typography';
import Box from './components/ui/Box/Box';

// Page/Layout Components
import Login from './components/auth/Login';
import AdminLayout from './layouts/AdminLayout';
import UserLayout from './layouts/UserLayout'; 
import AdminOverviewPage from './pages/AdminDashboard/AdminOverviewPage';
import AccessDataPage from './pages/UserDashboard/AccessDataPage';
import PrepareDataPage from './pages/UserDashboard/PrepareDataPage';
import JobManagementPage from './pages/UserDashboard/JobManagementPage';
import JobOverviewTab from './pages/UserDashboard/JobOverviewTab'; 
import RunLinkageTab from './pages/UserDashboard/RunLinkageTab';
import ResultsTab from './pages/UserDashboard/ResultsTab';
import MyAccountPage from './pages/UserDashboard/MyAccountPage';
import WelcomePage from './pages/UserDashboard/WelcomePage';

import CipherByNaluriLogo from './assets/cipher-by-naluri-logo.svg';


const HomeRedirect = () => {
  const { hasRole } = useAuth();
  if (hasRole('system_admin')) {
    return <Navigate to="/admin/dashboard/overview" replace />;
  }
  return <Navigate to="/user/dashboard" replace />;
};


function App() {
  const { user, isAuthenticated, isLoading, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const ProtectedRoute = ({ children, adminOnly = false }) => {
    if (isLoading) {
      return <Box className="AppLoading"><Spinner size="lg" /></Box>;
    }
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (adminOnly && !hasRole('system_admin')) {
      return <Navigate to="/" replace />; 
    }
    return children;
  };

  if (isLoading) {
    return (
      <Box className="AppLoading">
        <Spinner size="lg" />
        <Typography variant="body1" color="secondary" mt="md">Loading Application...</Typography>
      </Box>
    );
  }

  return (
    <div className="AppShell">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      {isAuthenticated && (
        <header className="AppHeader">
          <div className="HeaderContent ">
            <Link to="/" className="LogoLink">
              <img src={CipherByNaluriLogo} alt="CIPHER by Naluri" className="HeaderLogo" />
            </Link>
            <nav className="UserNav">
              <Link to="/user/account" style={{ textDecoration: 'none' }}>
                <Typography 
                  variant="body2" 
                  color="secondary" 
                  style={{ marginRight: 'var(--cipher-spacing-lg)', cursor: 'pointer' }}
                  className="user-welcome-link" 
                >
                  Welcome, {user?.first_name || user?.username}!
                </Typography>
              </Link>
              {hasRole('system_admin') && <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>Admin Portal</Button>}
              <Button variant="ghost" size="sm" onClick={() => navigate('/user/workspace')}>Workspace</Button>
              <Button variant="ghostDestructive" size="sm" onClick={logout}>Logout</Button>
            </nav>
          </div>
        </header>
      )}

      <main className="MainAppArea ">
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/admin/dashboard/*"
            element={<ProtectedRoute adminOnly={true}><AdminLayout><AdminOverviewPage /></AdminLayout></ProtectedRoute>}
          />

          <Route path="/user/*" element={<ProtectedRoute><UserLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<WelcomePage />} />
            <Route path="workspace" element={<JobManagementPage />}>

              <Route path=":batchId" element={<Navigate to="overview" replace />} />
              <Route path=":batchId/overview" element={<JobOverviewTab />} />
              <Route path=":batchId/run" element={<RunLinkageTab />} />
              <Route path=":batchId/results" element={<ResultsTab />} />
            </Route>

            <Route path="account" element={<MyAccountPage />} />
            
            <Route path="access-data" element={<AccessDataPage />} />
            
            <Route path="batch/:batchId/prepare/:dataSourceTypeName" element={<PrepareDataPage />} />

            <Route path="dashboard" element={<Navigate to="/user/workspace" replace />} />
            <Route path="run-linkage" element={<Navigate to="/user/workspace" replace />} />
            <Route path="batch/:batchId" element={<Navigate to="/user/workspace" replace />} />
            <Route path="batch/:batchId/run-linkage" element={<Navigate to="/user/workspace" replace />} />
          </Route>
          
          
          <Route path="/" element={isAuthenticated ? <HomeRedirect /> : <Navigate to="/login" replace />} />

          <Route path="*" element={
              <Box className="AppErrorFullScreen">
                <Typography variant="h1" gutterBottom>404 - Page Not Found</Typography>
                <Button variant="primary" onClick={() => navigate('/')} mt="lg">Go to Home</Button>
              </Box>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

export default App;